import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Box, makeStyles, Typography } from '@material-ui/core';

import { colors } from '../../../theme/color-palette';
import {
	BOGLE_LIGHT,
	BOGLE_MEDIUM,
	BOGLE_REGULAR,
	DATA_NOT_LOADING_MSG,
	API_FAILURE_MSG,
} from '../../../constants/constant';
import {
	convertToInternationalCurrencySystem,
	convertToTwoDecimal,
} from '../../../helper/convertAmount';
import ErrorScreenMessage from '../../ErrorScreenMessage/ErrorScreenMessage';
import { chartTitleChange } from '../../../helper/yoyUtils';

const { LIGHT_GREY, FONT_COLOR, POSITIVE_GROWTH_COLOR, NEGATIVE_GROWTH_COLOR } =
	colors;

export const useStyles = makeStyles(() => ({
	container: {
		width: '100%',
		border: `1px solid ${LIGHT_GREY}`,
		borderRadius: '8px',
		height: '430px',
	},
	header: {
		marginLeft: '6%',
		marginTop: '3%',
		fontSize: '18px',
		textAlign: 'left',
		fontFamily: BOGLE_REGULAR,
		color: FONT_COLOR,
	},
	items: {
		width: '88%',
		border: `1px solid ${LIGHT_GREY}`,
		borderRadius: '8px',
		height: '72px',
		marginLeft: '33px',
		marginTop: '16px',
	},
	itemName: {
		color: FONT_COLOR,
		fontSize: '16px',
		textAlign: 'left',
		fontFamily: BOGLE_MEDIUM,
		fontWeight: '500',
		lineHeight: '22px',
		marginLeft: '2%',
		marginTop: '1%',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		minWidth: '250px',
	},
	valueContainer: {
		display: 'flex',
		justifyContent: 'space-between',
		marginLeft: '2%',
		marginTop: '2%',
		marginRight: '2%',
	},
	value: {
		fontFamily: BOGLE_LIGHT,
		fontWeight: '300',
		fontSize: '24px',
		lineHeight: '33px',
		color: FONT_COLOR,
	},
	up: {
		fontFamily: BOGLE_LIGHT,
		fontStyle: 'normal',
		fontWeight: '300',
		fontSize: '20px',
		lineHeight: '28px',
		color: POSITIVE_GROWTH_COLOR,
	},
	down: {
		fontFamily: BOGLE_LIGHT,
		fontStyle: 'normal',
		fontWeight: '300',
		fontSize: '20px',
		lineHeight: '28px',
		color: NEGATIVE_GROWTH_COLOR,
	},
	yoyText: {
		fontSize: '12px',
		lineHeight: '17px',
		color: FONT_COLOR,
		fontFamily: BOGLE_LIGHT,
	},
	loader: {
		display: 'flex',
		height: '424px',
	},
	circularProgress: {
		margin: 'auto',
	},
}));

function RelativeCards({ name, cardData }) {
	const classes = useStyles();
	const isUnit = ['Relative Units'].includes(name);
	const { data, loading, errorCode } = cardData;

	if (!loading) {
		if (!data || !data[0]?.Item) {
			return (
				<ErrorScreenMessage errorMsg={DATA_NOT_LOADING_MSG} chartTitle={name} />
			);
		}
		if (errorCode !== 0) {
			return (
				<ErrorScreenMessage errorMsg={API_FAILURE_MSG} chartTitle={name} />
			);
		}
	}

	return (
		<Fragment>
			{loading ? (
				<Box className={classes.loader}>
					<CircularProgress className={classes.circularProgress} />
				</Box>
			) : (
				<Fragment>
					<Box className={classes.header}>{name}</Box>
					{data?.map((obj, index) => {
						// key will be Item, Category ...
						const key = Object.keys(obj)[0];
						if (key === 'Category') return;
						const prefix = isUnit ? '' : '$';
						const value = convertToInternationalCurrencySystem(
							obj[key]?.tyValue
						);
						let yoy = chartTitleChange(convertToTwoDecimal(obj[key]?.yoy));
						if (yoy !== 'NA') {
							yoy = obj[key]?.yoy > 0 ? `+${yoy}` : `-${yoy}`;
						}
						const title = `${key} : ${obj[key]?.name}`;
						return (
							<Fragment key={index}>
								<Box className={classes.items}>
									<Typography className={classes.itemName} title={title}>
										{title}
									</Typography>
									<Box className={classes.valueContainer}>
										<Typography
											className={
												classes.value
											}>{`${prefix}${value}`}</Typography>
										<Typography
											className={
												yoy?.includes('+') ? classes.up : classes.down
											}>
											{`${yoy}`}
											<span className={classes.yoyText}> (YoY)</span>
										</Typography>
									</Box>
								</Box>
							</Fragment>
						);
					})}
				</Fragment>
			)}
		</Fragment>
	);
}

RelativeCards.propTypes = {
	name: PropTypes.string,
	cardData: PropTypes.object,
};

export default RelativeCards;
